import system from 'durandal/system.js';

var hubspot = {
	chatToken: '',
	authUser: null,
	global: null,
	hasChatToken: false,
	tokenExpiresAt: 0,
	HUBSPOT_ID: null,
	canUseSupportChat: false,
};

hubspot.init = function (spec = {}) {
	hubspot.global = spec.global;
	hubspot.authUser = spec.authUser;
	hubspot.global.storageHandler.load(hubspot);
	hubspot.HUBSPOT_ID = spec.HUBSPOT_ID || process.env.HUBSPOT_ID || null;
	hubspot.canUseSupportChat = spec.canUseSupportChat || false;
};

hubspot.isEnabled = () => {
	return !!hubspot.HUBSPOT_ID;
};

hubspot.isChatSupported = () => {
	return !!(hubspot.isEnabled() && hubspot.canUseSupportChat);
};

hubspot.setCanUseSupportChat = (canUseSupportChat) => {
	hubspot.canUseSupportChat = canUseSupportChat;
};

hubspot._getFullId = function () {
	return 'app.hubspot';
};

hubspot.addTrackingCode = () => {
	const script = document.createElement('script');
	script.type = 'text/javascript';
	script.async = true;
	script.defer = true;
	script.src =
		(document.location.protocol === 'https:' ? 'https://' : 'http://') +
		'js.hs-scripts.com/' +
		hubspot.HUBSPOT_ID +
		'.js';
	const firstScript = document.getElementsByTagName('script')[0];
	if (firstScript && firstScript.parentNode) {
		firstScript.parentNode.insertBefore(script, firstScript);
	} else {
		document.head.appendChild(script);
	}
};

hubspot._toStorageData = function () {
	return Promise.resolve({
		chatToken: hubspot.chatToken,
		userId: hubspot.chatToken !== '' ? hubspot.authUser.userId : '',
		tokenExpiresAt: hubspot.tokenExpiresAt,
	});
};

hubspot._fromStorageData = function (data) {
	if (data) {
		if (data.chatToken != '' && data.tokenExpiresAt > Date.now()) {
			if (data.userId == hubspot.authUser.userId) {
				hubspot.chatToken = data.chatToken;
				hubspot.tokenExpiresAt = data.tokenExpiresAt;
			}
		}
	}
	return Promise.resolve();
};

hubspot.setChatToken = (token) => {
	hubspot.chatToken = token;
	hubspot.tokenExpiresAt = Date.now() + 10 * 60 * 60 * 1000; //10 hours
	hubspot.global.storageHandler.save(hubspot);
};

hubspot.clearChatToken = () => {
	hubspot.chatToken = '';
	hubspot.tokenExpiresAt = 0;
	hubspot.global.storageHandler.save(hubspot);
};

hubspot.getChatToken = () => {
	return hubspot.chatToken;
};

hubspot.hasChatToken = () => {
	return hubspot.chatToken !== '' && hubspot.tokenExpiresAt > Date.now();
};

hubspot.supportFallback = function (message = '') {
	const subject = encodeURIComponent('Support Request');
	const body = message ? encodeURIComponent(message.slice(0, 2000)) : '';
	const mailtoLink = `mailto:success@cheqroom.com?subject=${subject}&body=${body}`;
	window.location.href = mailtoLink;
};

hubspot.boot = function () {
	if (!hubspot.isEnabled() || !hubspot.isChatSupported()) return Promise.resolve();

	try {
		if (hubspot.hasChatToken()) {
			return hubspot._initChat();
		} else {
			return hubspot.loadChatToken();
		}
	} catch (err) {
		system.log('Error in hubspot.boot', err);
		return Promise.reject(err);
	}
};

hubspot.openChat = function (message = '') {
	if (!hubspot.isEnabled()) {
		system.log('HubSpot is not enabled');
		return Promise.resolve();
	}

	if (!hubspot.isChatSupported()) {
		system.log('Support chat is not supported, opening mailto fallback');
		hubspot.supportFallback(message);
		return Promise.resolve();
	}

	// Helper function to actually open the chat once widget is ready
	const tryOpenChat = () => {
		// Try to open chat using the Conversations API
		if (window.HubSpotConversations && window.HubSpotConversations.widget) {
			try {
				if (message) {
					window.HubSpotConversations.widget.open({ initialMessage: message });
				} else {
					window.HubSpotConversations.widget.open();
				}
				return true;
			} catch (err) {
				system.log('Error opening HubSpot chat widget', err);
			}
		}

		return false;
	};

	try {
		// First ensure HubSpot is booted and wait for initialization
		return hubspot
			.boot()
			.then(() => {
				// After boot completes, try to open chat immediately
				if (tryOpenChat()) {
					return;
				}

				// If widget isn't ready yet, wait for it
				// Preserve existing callbacks to avoid race conditions
				const originalOnReady = Array.isArray(window.hsConversationsOnReady)
					? window.hsConversationsOnReady
					: window.hsConversationsOnReady
						? [window.hsConversationsOnReady]
						: [];

				window.hsConversationsOnReady = [
					...originalOnReady,
					() => {
						try {
							if (window.HubSpotConversations && window.HubSpotConversations.widget) {
								if (message) {
									window.HubSpotConversations.widget.open({ initialMessage: message });
								} else {
									window.HubSpotConversations.widget.open();
								}
							}
						} catch (err) {
							system.log('Error opening HubSpot chat in onReady callback', err);
						}
					},
				];
			})
			.catch((err) => {
				system.log('Error in hubspot.openChat during boot', err);
			});
	} catch (err) {
		system.log('Error in hubspot.openChat', err);
		return Promise.resolve();
	}
};

hubspot.loadChatToken = () => {
	return hubspot.global
		.getDataSource('users')
		.call(hubspot.authUser.userId, 'createHubspotVisitorToken')
		.then((data) => {
			if (data.token) {
				hubspot.setChatToken(data.token);
				return hubspot._initChat();
			}
			return Promise.resolve();
		})
		.catch(() => {
			// Silently fail - return resolved promise
			return Promise.resolve();
		});
};

const onConversationsAPIReady = () => {
	try {
		if (window.HubSpotConversations && window.HubSpotConversations.widget) {
			window.HubSpotConversations.widget.load();
		} else {
			system.log('HubSpotConversations.widget is not available');
		}
	} catch (err) {
		system.log('Error in onConversationsAPIReady', err);
	}
};

hubspot._initChat = () => {
	if (!hubspot.hasChatToken()) return Promise.resolve();

	try {
		window.hsConversationsSettings = {
			identificationEmail: hubspot.authUser.email,
			identificationToken: hubspot.getChatToken(),
			loadImmediately: true,
		};

		hubspot.addTrackingCode();

		/*
			The API is housed in the window.HubSpotConversations object, which provides access to all available methods.
			The object is created by the HubSpot tracking code, but may not be available immediately on page load. 
			To defer accessing the API until it's initialized, you can use the window.hsConversationsOnReady helper.

			https://developers.hubspot.com/docs/reference/api/conversations/chat-widget-sdk#initializing
		*/
		if (window.HubSpotConversations) {
			onConversationsAPIReady();
			return Promise.resolve();
		} else {
			// Preserve existing callbacks to avoid race conditions
			const originalOnReady = Array.isArray(window.hsConversationsOnReady)
				? window.hsConversationsOnReady
				: window.hsConversationsOnReady
					? [window.hsConversationsOnReady]
					: [];

			// Create a promise that resolves when the widget is ready
			const initPromise = new Promise((resolve) => {
				const readyCallback = () => {
					onConversationsAPIReady();
					resolve();
				};

				window.hsConversationsOnReady = [...originalOnReady, readyCallback];
			});

			return initPromise;
		}
	} catch (err) {
		system.log('Error in hubspot._initChat', err);
		return Promise.reject(err);
	}
};

hubspot.shutdown = function () {
	if (!hubspot.isEnabled()) return;

	try {
		if (window.HubSpotConversations) {
			if (window.HubSpotConversations.widget) {
				try {
					window.HubSpotConversations.clear();
					window.HubSpotConversations.widget.remove();
				} catch (err) {
					system.log('Error removing HubSpot widget', err);
				}
			}
			// Reset ready callback for future initialization
			window.hsConversationsOnReady = [onConversationsAPIReady];
		}
	} catch (err) {
		system.log('Error in hubspot.shutdown', err);
	}
};

hubspot.logout = function () {
	try {
		hubspot.clearChatToken();
		if (window.HubSpotConversations) {
			try {
				window.HubSpotConversations.clear({ resetWidget: true });
			} catch (err) {
				system.log('Error clearing HubSpot on logout', err);
			}
		}
	} catch (err) {
		system.log('Error in hubspot.logout', err);
	}
};

hubspot.trackEvent = (eventName, properties = {}) => {
	if (window._hsq) {
		window._hsq.push(['trackEvent', { id: eventName, properties }]);
	}
};

hubspot.trackPageView = () => {
	if (window._hsq) {
		window._hsq.push(['trackPageView']);
	}
};

export default hubspot;
